<template>
  <div
    ref="container"
    class="k-title-container"
    :data-cy="column.downloadable ? '' : 'non-downloadable'"
  >
    <v-tooltip top>
      <template v-slot:activator="{ on: on, attr }">
        <div
          v-bind="attr"
          class="k-title-text"
          :class="{ 'k-title-sortable': showSortBtn && !disabled }"
          v-on="hasTooltip ? on : undefined"
          @click="
            showSortBtn && !disabled ? handleSortClick($event) : undefined
          "
        >
          <span>
            {{ title }}{{ sortIndicator }}
            <span v-if="sortCount" class="k-sort-order">{{ sortCount }}</span>
          </span>
        </div>
      </template>
      <span>{{ column.tooltip || title }}</span>
    </v-tooltip>
  </div>
</template>

<script>
export default {
  name: 'ColumnTitle',
  props: {
    field: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    // disable the inputs if there are no items
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Kendo is stupid and doesn't pass the whole column object, why???
    columns: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isLocked: false,
      sortDirection: '',
      clickCount: 0, // click 1x = sort to opposite dir, 2x = remove sort
      sortIndicator: '',
      sortCount: null,
      hasTooltip: false,
      /**
       * non-reactive props:
       * - showSortBtn {Boolean}
       */
    };
  },
  created() {
    // find this column object because stupid kendo does provide all the props
    const thisColumn = this.columns.find((col) => col.field === this.field);
    // only show the sort button if the column title is not an empty string
    this.showSortBtn = this.title.length > 0 && thisColumn.sortable !== false;
    this.column = thisColumn;
  },
  mounted() {
    this.setTooltip(true);
    // add the double click event listener via the DOM because we can't access the component as a ref
    const resizer = this.$refs.container
      .closest('th')
      .querySelector('.k-column-resizer');
    if (resizer)
      setTimeout(() => {
        if (this.$refs.container) {
          this.$refs.container
            .closest('th')
            .querySelector('.k-column-resizer')
            .addEventListener('dblclick', this.doubleClickExpand, false);
        }
      }, 500);
  },
  methods: {
    /**
     * Called by mounted
     * Also called externally from parent component when column is resized
     */
    // eslint-disable-next-line complexity
    setTooltip(onMounted = false) {
      let thWidth =
          onMounted && this.column.width
            ? this.column.width
            : this.$el.getBoundingClientRect().width,
        titleElement = this.$el.querySelector('.k-title-text'),
        titlePadding = titleElement
          ? parseInt(
              window
                .getComputedStyle(titleElement, null)
                .getPropertyValue('padding-left')
            )
          : '',
        titleWidth = titlePadding
          ? this.$el.querySelector('.k-title-text span').getBoundingClientRect()
              .width + titlePadding
          : '';
      if (titleWidth) {
        this.hasTooltip = this.column.alwaysShowTooltip
          ? true
          : thWidth === 0
          ? false
          : thWidth < titleWidth;
      }
    },
    handleSortClick(event) {
      // stop propagation because there is some unwanted kendo event firing
      event.stopPropagation();
      // if we already have 1 click
      if (this.clickCount === 1) {
        // set the sort to the opposite
        const sortDir = this.sortDirection === 'asc' ? 'desc' : 'asc';
        // update the reactive JSX props
        this.updateReactiveProps(sortDir);
        // update the state
        this.sortDirection = sortDir;
        // // set the sort
        this.$emit('change-sort', this.field, sortDir, 'column-filter');
        // increment the click count
        this.clickCount = 2;
      } else if (this.clickCount === 2) {
        // update the reactive jsx props
        this.updateReactiveProps('');
        // remove sort
        this.sortDirection = '';
        // remove the sort
        this.$emit('remove-sort', this.field, 'column-filter');
        // reset the click count
        this.clickCount = 0;
        this.hasTooltip = false;
      } else {
        // update the reactive JSX props
        this.updateReactiveProps('asc');
        // update the state
        this.sortDirection = 'asc';
        // // set the sort
        this.$emit('change-sort', this.field, 'asc', 'column-filter');
        // increment the click count
        this.clickCount = 1;
      }
    },
    /**
     * Called externally from parent component
     * @param {String} dir 'asc' | 'desc' | empty string
     */
    setSort(dir) {
      // update the reactive JSX props
      this.updateReactiveProps(dir);
      // set the sort direction
      this.sortDirection = dir;
      // reset click count
      this.clickCount = dir.length > 0 ? 1 : 0;
    },
    /**
     * Called externally from parent component
     * @param {Number} sortCount
     */
    setSortCount(sortCount = null) {
      this.sortCount = sortCount;
      // Updating tooltips visibility after sorting
      setTimeout(() => {
        this.setTooltip();
      }, 350);
    },
    /**
     * @param {String} dir 'asc' | 'desc' | ''
     */
    updateReactiveProps(dir) {
      if (dir === 'desc') {
        this.sortIndicator = ' ↓';
      } else if (dir === 'asc') {
        this.sortIndicator = ' ↑';
      } else {
        this.sortIndicator = '';
      }
    },
    doubleClickExpand(e) {
      e.stopPropagation();
      e.preventDefault();
      this.$emit('fit-column-content', this.column.field);
    },
  },
};
</script>

<style scoped lang="scss">
.k-title-container {
  align-items: center;
  display: flex;
  width: 100%;
}
.k-title-text {
  flex-grow: 1;
  overflow: hidden;
  padding: 8px 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--sb-table-header-text, #94a3b8);
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  &.k-title-sortable {
    cursor: pointer;
    user-select: none;
    &:hover {
      color: var(--sb-table-body-text, #1e293b);
    }
  }
}
.k-sort-order {
  font-size: 10px !important;
  height: 0px !important;
}
</style>
